import { useEffect, useState } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { NotFoundPage } from '../pages/NotFoundPage';
import { CustomerAuthGate } from './CustomerAuthGate';
import { CustomerLocaleProvider } from './CustomerLocaleProvider';
import { CustomerPortalHeader } from './components/CustomerPortalHeader';
import { InstallPrompt } from './components/InstallPrompt';
import { AccountHome } from './pages/AccountHome';
import { AssessmentBook } from './pages/AssessmentBook';
import { BookConfirmation, BookWizard } from './pages/BookWizard';
import { BookingDetail } from './pages/BookingDetail';
import { Bookings } from './pages/Bookings';
import { Credits } from './pages/Credits';
import { EmbedBookWizard } from './pages/EmbedBookWizard';
import { Household } from './pages/Household';
import { HouseholdJoin } from './pages/HouseholdJoin';
import { InsurancePage } from './pages/InsurancePage';
import { Landing } from './pages/Landing';
import { LiveStayPage } from './pages/LiveStayPage';
import { LoyaltyRewardsPage } from './pages/LoyaltyRewardsPage';
import { NotificationPreferences } from './pages/NotificationPreferences';
import { OfflinePage } from './pages/OfflinePage';
import { Packs } from './pages/Packs';
import { PayLinkPage } from './pages/PayLinkPage';
import { PaymentMethodsPage } from './pages/PaymentMethods';
import { Payments } from './pages/Payments';
import { PetDetail } from './pages/PetDetail';
import { Pets } from './pages/Pets';
import { Privacy } from './pages/Privacy';
import { Profile } from './pages/Profile';
import { RecurringVisits } from './pages/RecurringVisits';
import { SignIn } from './pages/SignIn';
import { SignInConsume } from './pages/SignInConsume';
import { SignInSent } from './pages/SignInSent';
import { WaiverAccept } from './pages/WaiverAccept';
import { WaiverSignByLink } from './pages/WaiverSignByLink';
import {
  activatePendingUpdate,
  attachInstallListeners,
  onSwUpdate,
  registerCustomerSW,
} from './pwa';
import { CustomerAuthProvider } from './useCustomerAuth';

/**
 * "Update available" banner — surfaces when registerCustomerSW detects a new
 * waiting service worker. One click triggers SKIP_WAITING + reload (handled
 * inside pwa.ts via controllerchange).
 */
function SwUpdateBanner() {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    return onSwUpdate(() => setAvailable(true));
  }, []);

  if (!available) return null;
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        left: '50%',
        top: 'max(1rem, env(safe-area-inset-top))',
        transform: 'translateX(-50%)',
        zIndex: 60,
        background: 'var(--surface, white)',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
        borderRadius: '12px',
        boxShadow: '0 10px 30px -10px oklch(0 0 0 / 0.18)',
        padding: '0.6rem 0.85rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.7rem',
        fontSize: '0.85rem',
      }}
    >
      <span>Update available.</span>
      <button
        type="button"
        onClick={() => activatePendingUpdate()}
        style={{
          padding: '0.35rem 0.7rem',
          borderRadius: '7px',
          border: 'none',
          background: 'var(--brand-primary, var(--primary, #004AAD))',
          color: 'white',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  );
}

/**
 * Pathless layout for the signed-in portal: the persistent header (tenant
 * name, back-to-account, sign out) above every `/account/*` page and the
 * `/book` wizard. Each child route keeps its own `CustomerAuthGate`, so the
 * header renders while the gate is still resolving.
 */
function PortalChrome() {
  return (
    <>
      <CustomerPortalHeader />
      <Outlet />
    </>
  );
}

export function CustomerApp() {
  useEffect(() => {
    // Capture the deferred install prompt + appinstalled before anything else
    // in the app mounts — the events fire once and we want them.
    attachInstallListeners();
    void registerCustomerSW();
  }, []);

  return (
    <ErrorBoundary context="customer">
      <BrowserRouter>
        <CustomerAuthProvider>
          <CustomerLocaleProvider>
            <SwUpdateBanner />
            <Routes>
              {/* Public auth routes */}
              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/sign-in/sent" element={<SignInSent />} />
              <Route path="/sign-in/consume" element={<SignInConsume />} />

              {/* Authenticated /account — every route below shares the
              persistent portal header via the pathless layout route. */}
              <Route element={<PortalChrome />}>
                <Route
                  path="/account"
                  element={
                    <CustomerAuthGate>
                      <AccountHome />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/bookings"
                  element={
                    <CustomerAuthGate>
                      <Bookings />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/bookings/:id"
                  element={
                    <CustomerAuthGate>
                      <BookingDetail />
                    </CustomerAuthGate>
                  }
                />
                {/* Migration 0096 — manage weekly daycare series. */}
                <Route
                  path="/account/recurring-visits"
                  element={
                    <CustomerAuthGate>
                      <RecurringVisits />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/payments"
                  element={
                    <CustomerAuthGate>
                      <Payments />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/payment-methods"
                  element={
                    <CustomerAuthGate>
                      <PaymentMethodsPage />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/credits"
                  element={
                    <CustomerAuthGate>
                      <Credits />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/packs"
                  element={
                    <CustomerAuthGate>
                      <Packs />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/loyalty"
                  element={
                    <CustomerAuthGate>
                      <LoyaltyRewardsPage />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/rewards"
                  element={
                    <CustomerAuthGate>
                      <LoyaltyRewardsPage />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/pets"
                  element={
                    <CustomerAuthGate>
                      <Pets />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/pets/:id"
                  element={
                    <CustomerAuthGate>
                      <PetDetail />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/pets/:id/assessment"
                  element={
                    <CustomerAuthGate>
                      <AssessmentBook />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/profile"
                  element={
                    <CustomerAuthGate>
                      <Profile />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/notifications"
                  element={
                    <CustomerAuthGate>
                      <NotificationPreferences />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/insurance"
                  element={
                    <CustomerAuthGate>
                      <InsurancePage />
                    </CustomerAuthGate>
                  }
                />
                <Route
                  path="/account/privacy"
                  element={
                    <CustomerAuthGate>
                      <Privacy />
                    </CustomerAuthGate>
                  }
                />
                {/* Migration 0102 — household co-owners. */}
                <Route
                  path="/account/household"
                  element={
                    <CustomerAuthGate>
                      <Household />
                    </CustomerAuthGate>
                  }
                />
                {/* Waiver acceptance gate — bypass the version check so the user
              can submit a signature without an infinite redirect loop. */}
                <Route
                  path="/account/waiver"
                  element={
                    <CustomerAuthGate skipWaiverCheck>
                      <WaiverAccept />
                    </CustomerAuthGate>
                  }
                />

                {/* Sub-spec 01b — /book wizard.
              PUBLIC: a visitor can check availability + price with no session.
              The wizard itself asks for a magic-link sign-in at the step that
              creates the booking (see BookWizard's `account` step), so the
              auth requirement lives inside the wizard, not on the route. The
              header shows "Sign in" for guests and the back link once signed in. */}
                <Route path="/book" element={<BookWizard />} />
                <Route
                  path="/book/:bookingId/confirmation"
                  element={
                    <CustomerAuthGate>
                      <BookConfirmation />
                    </CustomerAuthGate>
                  }
                />
              </Route>

              {/* PWA offline shell — public, no auth gate (the SW may navigate
              here when both network + cached auth state are unreachable). No
              header: it must render with nothing fetched. */}
              <Route path="/account/offline" element={<OfflinePage />} />

              {/* Sub-spec 01b Plan 4 — embed widget. Cross-origin iframe target.
              Wraps the same wizard but with chromeless presentation and
              postMessage emitters for the host page. Public for the same
              reason as /book — sign-in happens inside the wizard. */}
              <Route path="/embed/book" element={<EmbedBookWizard />} />

              {/* Sub-spec 01d — public live-stay page. NO AuthGate; the bst_<22>
              token in the URL is the auth. */}
              <Route path="/stay/:token" element={<LiveStayPage />} />

              {/* Waiver sign-by-link — public, NO AuthGate; the wst_ token in
              the URL is the auth. Staff mint it from the customer page. */}
              <Route path="/waiver/sign/:token" element={<WaiverSignByLink />} />

              {/* Household invite landing — public, NO AuthGate; the hh_ token is
              the auth and the page offers a magic-link sign-in that returns here. */}
              <Route path="/household/join/:token" element={<HouseholdJoin />} />
              {/* Migration 0098 — public text-to-pay page. NO AuthGate; the pl_
              token in the URL is the auth. */}
              <Route path="/pay/:token" element={<PayLinkPage />} />

              {/* Marketing landing — public, no auth gate */}
              <Route path="/" element={<Landing />} />

              {/* Branded 404 page for any unknown URL on the customer subdomain. */}
              <Route path="*" element={<NotFoundPage context="customer" />} />
            </Routes>
            <InstallPrompt />
          </CustomerLocaleProvider>
        </CustomerAuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
